'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetCreatedMarketsAndProposalsField } from '@morfi/backend-sdk-v2/client';
import { useFormatter, useTranslations } from 'next-intl';
import type { RefCallback } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem } from '@/components/ui/data-list-item';
import { capitalizeLeading, resolveProposalCategory } from '@/config/proposal-display';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';
export function CreatedMarketRow(t0) {
  const $ = _c(59);
  const {
    pubkey,
    backerRewards,
    backerRewardsRef
  } = t0;
  const t = useTranslations("profile.created");
  const tRewards = useTranslations("profile.rewards");
  const tMetrics = useTranslations("competitions.metric");
  const tCategories = useTranslations("categories");
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  const marketType = useGetCreatedMarketsAndProposalsField(pubkey, "marketType");
  const title = useGetCreatedMarketsAndProposalsField(pubkey, "title");
  const category = useGetCreatedMarketsAndProposalsField(pubkey, "category");
  const resolvedCategory = category ? resolveProposalCategory(category) : null;
  const createdAt = useGetCreatedMarketsAndProposalsField(pubkey, "createdAt");
  const image = useGetCreatedMarketsAndProposalsField(pubkey, "coverImageUrl");
  const nsfw = useGetCreatedMarketsAndProposalsField(pubkey, "coverImageNsfw");
  const volume = useGetCreatedMarketsAndProposalsField(pubkey, "volumeTotal");
  const prizePool = useGetCreatedMarketsAndProposalsField(pubkey, "prizePool");
  const href = `/markets/${pubkey}`;
  const intent = useIntentRoutePrefetch(href);
  let t1;
  if ($[0] !== formatD6) {
    t1 = function formatAmount(value) {
      return formatD6(value.toString());
    };
    $[0] = formatD6;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatAmount = t1;
  if (title === undefined || volume === undefined || prizePool === undefined) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton className="h-24 w-full rounded-control" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const T0 = DataListItem;
  const t2 = true;
  const t3 = resolvedCategory !== null ? tCategories(resolvedCategory.id as keyof IntlMessages['categories']) : category ? capitalizeLeading(category) : null;
  let t4;
  if ($[3] !== createdAt || $[4] !== formatter) {
    t4 = createdAt === undefined ? null : <>{" "}·{" "}<time dateTime={createdAt}>{formatter.dateTime(new Date(createdAt), {
          dateStyle: "medium",
          timeZone: "UTC"
        })}</time></>;
    $[3] = createdAt;
    $[4] = formatter;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = category ?? "";
  const t7 = image ?? null;
  const t8 = nsfw === true;
  let t9;
  if ($[9] !== pubkey || $[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey,
      category: t6,
      coverImageUrl: t7,
      nsfw: t8
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[9] = pubkey;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== marketType || $[15] !== t9) {
    t10 = <ThumbWithOrigin marketType={marketType}>{t9}</ThumbWithOrigin>;
    $[14] = marketType;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== tMetrics) {
    t11 = tMetrics("volume");
    $[17] = tMetrics;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== volume) {
    t12 = BigInt(volume);
    $[19] = volume;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== formatAmount || $[22] !== t12) {
    t13 = <AnimatedBigInt value={t12} format={formatAmount} />;
    $[21] = formatAmount;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13) {
    t14 = {
      key: "volume",
      label: t11,
      value: t13
    };
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t) {
    t15 = t("prizePool");
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== prizePool) {
    t16 = BigInt(prizePool);
    $[29] = prizePool;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== formatAmount || $[32] !== t16) {
    t17 = <AnimatedBigInt value={t16} format={formatAmount} />;
    $[31] = formatAmount;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t15 || $[35] !== t17) {
    t18 = {
      key: "prizePool",
      label: t15,
      value: t17
    };
    $[34] = t15;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== backerRewards || $[38] !== backerRewardsRef || $[39] !== formatAmount || $[40] !== marketType || $[41] !== tRewards) {
    t19 = marketType === "featured" ? [] : [{
      key: "backerRewards",
      label: tRewards("title"),
      value: <div ref={backerRewardsRef} className="text-feedback-success-content">{backerRewards === undefined ? <Skeleton className="h-5 w-16" /> : <AnimatedBigInt value={BigInt(backerRewards)} format={formatAmount} />}</div>
    }];
    $[37] = backerRewards;
    $[38] = backerRewardsRef;
    $[39] = formatAmount;
    $[40] = marketType;
    $[41] = tRewards;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t14 || $[44] !== t18 || $[45] !== t19) {
    t20 = [t14, t18, ...t19];
    $[43] = t14;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== pubkey || $[48] !== title) {
    t21 = <ShareButton marketPubkey={pubkey} marketTitle={title} />;
    $[47] = pubkey;
    $[48] = title;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== T0 || $[51] !== href || $[52] !== intent || $[53] !== t10 || $[54] !== t20 || $[55] !== t21 || $[56] !== t5 || $[57] !== title) {
    t22 = <T0 href={href} mobileMetricsBelowText={t2} intent={intent} ariaLabel={title} primaryText={title} secondaryText={t5} image={t10} metrics={t20} action={t21} />;
    $[50] = T0;
    $[51] = href;
    $[52] = intent;
    $[53] = t10;
    $[54] = t20;
    $[55] = t21;
    $[56] = t5;
    $[57] = title;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}